import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import { useEffect, useState } from "react";
import authHeader from "services/auth-header";
import API from "services/axios";
import CustomerFillPickers from "./CustomerFillPickers";
import { trackingUrlFor } from "./courier";

// "Fill contact from…" / "Fill address from…" are hidden for now (user,
// 2026-10-22: "only leave select customer"). true brings them back.
const SHOW_FILL_PICKERS = false;

// A section's title, set apart from the field labels under it (user,
// 2026-10-22: "WO Customer" the section read like a duplicate of the field).
const SectionTitle = ({ children }) => (
  <h5 className="text-primary fw-bold border-bottom pb-1 mt-3 mb-2">
    {children}
  </h5>
);

/**
 * Shipments (formerly Tracking Info) -- the Add / Edit shipment form.
 *
 * Layout (user, 2026-10-21): the first row (courier, tracking #, ship date);
 * Status, Voided and Tracking Details -- READ ONLY, one line, filled by the
 * truck button / Bulk Update Status, never typed; the shipment; the ADDRESS
 * the tracker sent (Company Name and Contact Name included); the contact's
 * phone and email; and last the WO Customer -- the ERP link, which can fill
 * the contact and address from that customer's own records.
 *
 * Round 2: a shipment typed in by hand (sourceFile "manual"), the whole form.
 * Round 3 (combine DHL): every row also has a customer LINK, a location, a
 * status and Tracking Details. Rows the UPS tracker sent (WorldShip) use the
 * same full form (user, 2026-10-16) -- that is also how a customer gets
 * attached when the W-ID was blank. Only manual rows can be deleted.
 *
 * The form holds a DAY. On Edit the ship date is sent only when it was
 * changed, so a WorldShip row keeps the time it shipped.
 *
 * `customerId` is the PER-COMPANY customer number (customers.customerId, the
 * "C-n"), never the table key customers.id (user, 2026-10-19: "we should
 * always save the per-company Ids").
 */

export const MANUAL_SOURCE = "manual";
export const isManualRow = (row) => row?.sourceFile === MANUAL_SOURCE;

// Rows copied from the old DHL table (nova-api sql/upstrackings-merge-dhl-rows.sql)
// are stamped "dhl-migration:<DHL id>".
export const DHL_COPY_PREFIX = "dhl-migration:";

// Rows copied from a Nova Shipping invoice by its "Copy to Shipments" button
// (2026-10-22) are stamped "nova-shipping:<invoice>". They can be deleted
// like a manual row -- the address can then be copied again.
export const NOVA_SHIPPING_PREFIX = "nova-shipping:";
export const isNovaShippingRow = (row) =>
  String(row?.sourceFile || "").startsWith(NOVA_SHIPPING_PREFIX);
export const isDeletableRow = (row) => isManualRow(row) || isNovaShippingRow(row);

// A ship DAY with no time of its own -- typed on the form, or copied from DHL,
// which only ever saved a day. Shown date only; the stored midday is not a
// real time. DISPLAY ONLY: deleting and the editable fields still go by
// isManualRow.
export const isDateOnlyRow = (row) =>
  isManualRow(row) ||
  String(row?.sourceFile || "").startsWith(DHL_COPY_PREFIX);

export const STATUS_OPTIONS = [
  { value: "in-transit", label: "In-Transit" },
  { value: "delivered", label: "Delivered" },
];

// "Shipment Type" is the `service` column -- WorldShip sends "Ground",
// "Next Day Air"... Suggestions only; anything can be typed.
const SHIPMENT_TYPES = ["Ground", "Next Day", "2nd Day", "Express"];

// The columns a WorldShip row carries, plus phone / email (2026-10-21).
// "Company Name" is the customerName COLUMN: the name the tracker sent, the
// shipment's own -- not the WO Customer link below.
const SECTIONS = [
  {
    title: "Shipment",
    // All four on one row (user, 2026-10-22).
    colClass: "col-md-3",
    fields: [
      { name: "service", label: "Shipment Type", list: "manual-shipment-types" },
      { name: "packages", label: "Packages", type: "number", step: "1" },
      { name: "weight", label: "Weight (lbs)", type: "number", step: "0.01" },
      { name: "shippingCost", label: "Shipping Cost ($)", type: "number", step: "0.01" },
    ],
  },
  {
    title: "Address",
    fields: [
      { name: "customerName", label: "Company Name" },
      { name: "contactName", label: "Contact Name" },
      { name: "address1", label: "Address 1" },
      { name: "address2", label: "Address 2" },
      { name: "city", label: "City" },
      { name: "state", label: "State" },
      { name: "zip", label: "Zip" },
      { name: "country", label: "Country" },
    ],
  },
];
// No Contact (Phone / Email) section (user, 2026-10-22): the UPS tracker
// never sends either. A row that has them keeps them -- they ride along
// unseen, because the API saves a missing field as empty.
const UNSHOWN_FIELDS = ["contactPhone", "contactEmail"];
const ALL_FIELDS = [
  ...SECTIONS.flatMap((s) => s.fields.map((f) => f.name)),
  ...UNSHOWN_FIELDS,
];

const pad = (n) => String(n).padStart(2, "0");
// The day as the viewer's calendar shows it -- the same day the table shows.
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const today = () => toDateInput(new Date());

const text = (v) => (v === null || v === undefined ? "" : String(v));

const initialValues = (row) => ({
  // Older WorldShip rows have no courierId and are SHOWN as the UPS tracker
  // (row.courier, resolved by the API) -- prefill that, so they save as it.
  courierId: String(row?.courierId || row?.courier?.id || ""),
  trackingNumber: row?.trackingNumber || "",
  shipDate: row ? toDateInput(row.shipDate) : today(),
  void: row?.void === "Y",
  customerId: row?.customerId ? String(row.customerId) : "",
  status: row?.status || "",
  trackingInfo: row?.trackingInfo || "",
  ...Object.fromEntries(ALL_FIELDS.map((f) => [f, text(row?.[f])])),
});

export const customerLabel = (c) =>
  c ? `C-${c.customerId} · ${c.customerName || ""}` : "";

export default function ManualEntryForm({ row, onSaved, onCancel }) {
  const isEdit = !!row;
  const fromTracker = isEdit && !isManualRow(row);

  const [initial] = useState(() => initialValues(row));
  const [values, setValues] = useState(initial);
  const [customer, setCustomer] = useState(row?.customer || null);
  const [couriers, setCouriers] = useState([]);
  // The location is the customer's own (set by the API from the customer);
  // shown here, not chosen.
  const [customerLocation, setCustomerLocation] = useState(
    row?.location?.locationName || "",
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [trackNote, setTrackNote] = useState("");

  useEffect(() => {
    const fail = (err) => setError(err?.response?.data?.message || err.message);
    API.get("/courier-services", { headers: authHeader() })
      .then(({ data }) => setCouriers(Array.isArray(data) ? data : []))
      .catch(fail);
  }, []);

  const set = (name) => (e) => setValues((prev) => ({ ...prev, [name]: e.target.value }));

  const searchCustomers = async (searchText, page) => {
    const { data } = await API.post(
      "/customers",
      { page, query: searchText },
      { headers: authHeader() },
    );
    return {
      results: data?.result || [],
      total: data?.result?.length || 0,
      totalPages: data?.pages || 0,
    };
  };

  // Picking a customer links it and takes the customer's location.
  const pickCustomer = (c) => {
    setCustomer(c);
    setCustomerLocation(c?.location?.name || "");
    setValues((prev) => ({
      ...prev,
      // the per-company number, not c.id -- see the note at the top
      customerId: String(c.customerId),
    }));
  };
  const clearCustomer = () => {
    setCustomer(null);
    setCustomerLocation("");
    setValues((prev) => ({ ...prev, customerId: "" }));
  };

  // The truck button: latest status from the courier's API (DHL / UPS, chosen
  // by the courier's tracking URL in Settings). Fills Status and Tracking
  // Details only -- nothing is saved until Add / Update.
  const getTracking = async () => {
    setError("");
    setTrackNote("");
    if (!values.courierId) return setError("Choose the courier first");
    if (!values.trackingNumber.trim()) return setError("Enter the tracking # first");
    try {
      setTracking(true);
      const { data } = await API.post(
        "/ups-tracking/track",
        { courierId: values.courierId, trackingNumber: values.trackingNumber },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Tracking failed");
      setValues((prev) => ({
        ...prev,
        status: data.data.status || prev.status,
        trackingInfo: data.data.trackingInfo || prev.trackingInfo,
      }));
      setTrackNote(`Filled from ${String(data.data.provider || "").toUpperCase()} — press ${isEdit ? "Update" : "Add"} to save.`);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setTracking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!values.courierId) return setError("Courier is required");
    if (!values.trackingNumber.trim()) return setError("Tracking # is required");
    if (!values.shipDate) return setError("Ship Date is required");

    const body = {
      ...values,
      void: values.void ? "Y" : "",
      ...(isEdit
        ? {
            id: row.id,
            // Unchanged day: leave it out and the row keeps its own ship date
            // (and a WorldShip row the time it shipped).
            shipDate: values.shipDate === initial.shipDate ? "" : values.shipDate,
          }
        : {}),
    };

    try {
      setSaving(true);
      const { data } = isEdit
        ? await API.patch("/ups-tracking", body, { headers: authHeader() })
        : await API.post("/ups-tracking", body, { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Save failed");
      onSaved(data.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const label = (id, children) => (
    <label htmlFor={id} className="form-label mb-0 small fw-bold">
      {children}
    </label>
  );

  const input = ({ name, label: text, type = "text", step, list }, colClass = "col-md-4") => (
    <div className={`${colClass} mb-2`} key={name}>
      {label(`manual-${name}`, text)}
      <input
        id={`manual-${name}`}
        type={type}
        step={step}
        list={list}
        min={type === "number" ? "0" : undefined}
        className="form-control form-control-sm"
        value={values[name]}
        onChange={set(name)}
      />
    </div>
  );

  return (
    <form onSubmit={submit} noValidate>
      <h4 className="mb-3">{isEdit ? "Edit Shipment" : "Add Shipment"}</h4>

      {fromTracker && (
        <p className="small text-dark mb-2">
          <i className="mdi mdi-truck-delivery-outline me-1"></i>
          {isNovaShippingRow(row)
            ? `Copied from Nova Shipping, invoice ${row.sourceFile.slice(NOVA_SHIPPING_PREFIX.length)}`
            : `Sent by the UPS tracker${row.sourceFile ? ` (${row.sourceFile})` : ""}`}
          {row.woId ? ` · W-${row.woId}` : ""}
        </p>
      )}

      <div className="row">
          <div className="col-md-4 mb-2">
            {label("manual-courierId", "Courier *")}
            <select
              id="manual-courierId"
              className="form-select form-select-sm"
              value={values.courierId}
              onChange={set("courierId")}
            >
              <option value="">Select courier...</option>
              {couriers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-2">
            {label("manual-trackingNumber", "Tracking # *")}
            <div className="d-flex gap-1">
              <input
                id="manual-trackingNumber"
                className="form-control form-control-sm"
                value={values.trackingNumber}
                onChange={set("trackingNumber")}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-primary text-nowrap"
                title="Get tracking from the courier"
                aria-label="Get tracking"
                disabled={tracking}
                onClick={getTracking}
              >
                <i className={`mdi ${tracking ? "mdi-loading mdi-spin" : "mdi-truck-fast-outline"}`}></i>
              </button>
              {/* The courier's tracking page in a new tab (user, 2026-10-27),
                  the same link as the number on the list. */}
              {values.trackingNumber?.trim() ? (
                <a
                  className="btn btn-sm btn-outline-primary text-nowrap"
                  title="Open tracking link"
                  aria-label="Open tracking link"
                  href={trackingUrlFor({
                    trackingNumber: values.trackingNumber.trim(),
                    courier: couriers.find(
                      (c) => String(c.id) === String(values.courierId),
                    ),
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="mdi mdi-open-in-new"></i>
                </a>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary text-nowrap"
                  title="Open tracking link"
                  aria-label="Open tracking link"
                  disabled
                >
                  <i className="mdi mdi-open-in-new"></i>
                </button>
              )}
            </div>
          </div>
          <div className="col-md-4 mb-2">
            {label("manual-shipDate", "Ship Date *")}
            <input
              id="manual-shipDate"
              type="date"
              className="form-control form-control-sm"
              value={values.shipDate}
              onChange={set("shipDate")}
            />
          </div>
        </div>

      {/* Status, Voided, Tracking Details. Tracking Details is READ ONLY and
          one line (user, 2026-10-21): only the truck button and Bulk Update
          Status write it, from the courier. */}
      <div className="row align-items-end">
        <div className="col-md-3 mb-2">
          {label("manual-status", "Status")}
          <select
            id="manual-status"
            className="form-select form-select-sm"
            value={values.status}
            onChange={set("status")}
          >
            <option value="">—</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 mb-2">
          <div className="form-check mb-1">
            <input
              id="manual-void"
              type="checkbox"
              className="form-check-input"
              checked={values.void}
              onChange={(e) => setValues((prev) => ({ ...prev, void: e.target.checked }))}
            />
            <label htmlFor="manual-void" className="form-check-label small fw-bold">
              Voided
            </label>
          </div>
        </div>
        <div className="col-md-7 mb-2">
          {label("manual-trackingInfo", "Tracking Details")}
          <input
            id="manual-trackingInfo"
            type="text"
            readOnly
            tabIndex={-1}
            className="form-control form-control-sm bg-light"
            value={values.trackingInfo}
            placeholder="Filled by the truck button"
          />
        </div>
      </div>

      <datalist id="manual-shipment-types">
        {SHIPMENT_TYPES.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      {SECTIONS.map((section) => (
        <div key={section.title}>
          <SectionTitle>{section.title}</SectionTitle>
          <div className="row">
            {section.fields.map((f) => input(f, section.colClass))}
          </div>
        </div>
      ))}

      {/* WO Customer: the ERP link (its location comes with it). Last, after
          the tracker's own text (user, 2026-10-21). */}
      <SectionTitle>WO Customer</SectionTitle>
      <div className="mb-2">
        <span className="form-label mb-0 small fw-bold d-block">Customer</span>
        <SearchableSelect
          searchFunction={searchCustomers}
          value={customer}
          selectedLabel={customerLabel(customer)}
          placeholder="Search customer..."
          RenderItem={({ item }) => (
            <div
              className="px-3 py-1 border-bottom d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={() => pickCustomer(item)}
            >
              <span className="text-muted text-nowrap">C-{item.customerId}</span>
              <span className="fw-bold w-100 text-nowrap">{item.customerName}</span>
              <span className="text-muted text-nowrap">{item?.location?.name || ""}</span>
            </div>
          )}
          ResetComponent={() => (
            <div className="px-2 text-danger" style={{ cursor: "pointer" }} onClick={clearCustomer}>
              Clear
            </div>
          )}
        />
        <small className="text-muted" data-testid="customer-location">
          Location: {customerLocation || "—"}
        </small>
        {SHOW_FILL_PICKERS && (
          <CustomerFillPickers
            customerNumber={values.customerId}
            customer={customer}
            onFill={(fields) => setValues((prev) => ({ ...prev, ...fields }))}
          />
        )}
      </div>

      {trackNote && !error && (
        <div className="alert alert-success py-1 my-2" role="status">
          {trackNote}
        </div>
      )}

      {error && (
        <div className="alert alert-danger py-1 my-2" role="alert">
          {error}
        </div>
      )}

      <div className="d-flex justify-content-end gap-1 mt-3">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving..." : isEdit ? "Update" : "Add"}
        </button>
      </div>
    </form>
  );
}
